import { useState, useEffect } from "react";
import styled, { css, keyframes } from "styled-components/macro";
import Confetti from "react-dom-confetti";
import { BellCircle } from "../Icons";
import { THEME_COLOR, SUCCESS_COLOR } from "../../vars";
import Stack from "../Stack";
import bellringSrc from "./bellring.mp3";

export default function MilestoneAlert({
  active = true,
  children,
  sound = true,
  fill = false,
  horizontal = false,
  invert = false,
  confetti = !invert,
}) {
  const [bellRingAudio] = useState(() => {
    if (!sound) return null;
    const bellRingAudio = new Audio();
    bellRingAudio.src = bellringSrc;
    return bellRingAudio;
  });

  useEffect(() => {
    if (active && sound) bellRingAudio.play();
  }, [active]);

  return (
    <MilestonePanel
      show={active}
      fill={fill}
      invert={invert}
      horizontal={horizontal}
    >
      {confetti && (
        <div style={{ position: "absolute", top: "50%", left: "50%" }}>
          <Confetti config={confettiConfig} active={active} />
        </div>
      )}
      <Stack horizontal={horizontal} center={horizontal}>
        <BigBell
          invert={invert}
          style={{ animationIterationCount: fill ? undefined : "infinite" }}
        />
        {children}
      </Stack>
    </MilestonePanel>
  );
}

const ringKeyframes = keyframes`
  0% { transform: rotate(0); }
  1% { transform: rotate(15deg); }
  3% { transform: rotate(-14deg); }
  5% { transform: rotate(17deg); }
  7% { transform: rotate(-16deg); }
  9% { transform: rotate(15deg); }
  11% { transform: rotate(-14deg); }
  13% { transform: rotate(13deg); }
  15% { transform: rotate(-12deg); }
  17% { transform: rotate(11deg); }
  19% { transform: rotate(-10deg); }
  21% { transform: rotate(9deg); }
  23% { transform: rotate(-8deg); }
  25% { transform: rotate(7deg); }
  27% { transform: rotate(-6deg); }
  29% { transform: rotate(5deg); }
  31% { transform: rotate(-4deg); }
  33% { transform: rotate(3deg); }
  35% { transform: rotate(-2deg); }
  37% { transform: rotate(1deg); }
  39% { transform: rotate(-0.5deg); }
  41% { transform: rotate(0.5deg); }

  43% { transform: rotate(0); }
  100% { transform: rotate(0); }
`;

const BigBell = styled(BellCircle)`
  width: 48px;
  height: 48px;
  color: ${THEME_COLOR};
  ${(p) =>
    p.invert &&
    css`
      color: white;
    `}
`;

const MilestonePanel = styled.div`
  background: white;
  color: #333;
  transform: scale(1.3);
  opacity: 0;
  transition-duration: 0.4s;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  z-index: 3;
  pointer-events: none;
  padding: 10px 0;
  ${(p) =>
    p.fill &&
    css`
      padding: 40px;
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
    `}
  ${(p) =>
    p.invert &&
    css`
      color: white;
      background: ${SUCCESS_COLOR};
      small {
        color: white !important;
      }
    `}
  ${(p) =>
    p.horizontal &&
    css`
      text-align: left;
    `}
  ${(p) =>
    p.show &&
    css`
      transform: scale(1);
      opacity: 1;
      pointer-events: auto;
      ${BigBell} {
        animation: ${ringKeyframes} 5s 0.1s ease-in-out;
      }
    `};
`;

const confettiConfig = {
  angle: 90,
  spread: 90,
  startVelocity: 30,
  elementCount: 30,
  dragFriction: 0.15,
  duration: 5000,
  stagger: 5,
  width: "10px",
  height: "10px",
  perspective: "500px",
  colors: ["#a864fd", "#29cdff", "#78ff44", "#ff718d", "#fdff6a"],
};
